import type { Katalog, KatalogQuelle, Operation, UiVoll } from "@gui-creator/gemeinsam";
import { ChevronRight } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import type { PlatzhalterInfo } from "./TextFeld.tsx";

export type Aendern = (ops: Operation[]) => void;

export type Kontext = {
  ui: UiVoll;
  katalog: Katalog;
  aendern: Aendern;
  darf: boolean;
  verschieben?: (von: number) => void;
};

/** Übernimmt gesetzte Felder; null entfernt ein Feld. */
export function mische<T extends object>(alt: T, a: Record<string, unknown>): T {
  const neu: Record<string, unknown> = { ...(alt as Record<string, unknown>) };
  for (const [k, v] of Object.entries(a)) {
    if (v === null) delete neu[k];
    else if (v !== undefined) neu[k] = v;
  }
  return neu as T;
}

export function quelleVon(katalog: Katalog, schluessel: string | undefined): KatalogQuelle | undefined {
  return katalog.quellen.find((q) => q.schluessel === schluessel);
}

const SEITE: PlatzhalterInfo[] = [
  { name: "seite", beschreibung: "Aktuelle Seite" },
  { name: "seiten", beschreibung: "Anzahl der Seiten" },
];

export function menuePlatzhalter(k: Kontext): PlatzhalterInfo[] {
  const q = quelleVon(k.katalog, k.ui.daten.quelle);
  return [...(q?.menueWerte ?? []), ...SEITE];
}

export function eintragPlatzhalter(k: Kontext, quelle: string): PlatzhalterInfo[] {
  const q = quelleVon(k.katalog, quelle);
  return [...(q?.eintragWerte ?? []), ...menuePlatzhalter(k)];
}

export function Abschnitt({ titel, children, aktion, offen: anfangsOffen = true, region = false }: { titel: ReactNode; children: ReactNode; aktion?: ReactNode; offen?: boolean; region?: boolean }) {
  const [offen, setOffen] = useState(anfangsOffen);
  const kennung = useId();
  return (
    <section className="abschnitt" {...(region ? { "aria-labelledby": kennung } : {})}>
      <header className="abschnitt-kopf">
        <button type="button" id={kennung} className="abschnitt-titel" aria-expanded={offen} onClick={() => setOffen(!offen)}>
          <ChevronRight size={10} className="abschnitt-pfeil" aria-hidden="true" />
          {titel}
        </button>
        {aktion}
      </header>
      {offen && <div className="abschnitt-inhalt">{children}</div>}
    </section>
  );
}

export function ZustandReiter({ zustaende, aktiv, onWahl, belegt }: { zustaende: string[]; aktiv: string | null; onWahl: (z: string | null) => void; belegt: (z: string) => boolean }) {
  if (zustaende.length === 0) return null;
  return (
    <div className="zustand-reiter" role="tablist" aria-label="Zustand bearbeiten">
      {[null, ...zustaende].map((z) => (
        <button key={z ?? "-"} type="button" role="tab" aria-selected={aktiv === z} className={aktiv === z ? "aktiv" : ""} onClick={() => onWahl(z)}>
          {z ?? "Grundfassung"}
          {z && belegt(z) && <span className="zustand-punkt" aria-label="hat Abweichungen" />}
        </button>
      ))}
    </div>
  );
}
